import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import type { PublicPaymentOptions } from "@/hooks/usePublicPaymentOptions";

export type PaymentAmount = "deposit" | "full";

interface PaymentAmountChoiceProps {
  value: PaymentAmount;
  onChange: (value: PaymentAmount) => void;
  options: PublicPaymentOptions;
  /** Keeps radio ids unique when the page renders the choice twice. */
  idPrefix: string;
}

const OPTION_CLASS =
  "flex items-center justify-between gap-3 border rounded-lg p-4 cursor-pointer hover:bg-muted/50 transition-colors [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5";

// Deposit vs pay-in-full, shown before the payment method on the public quote
// page (#983). Styled like the payment-method radios it sits above.
export function PaymentAmountChoice({ value, onChange, options, idPrefix }: PaymentAmountChoiceProps) {
  return (
    <RadioGroup
      value={value}
      onValueChange={(v) => onChange(v as PaymentAmount)}
      className="space-y-3"
      aria-label="Payment amount"
    >
      <Label htmlFor={`${idPrefix}-amount-deposit`} className={OPTION_CLASS}>
        <span className="flex items-center gap-3">
          <RadioGroupItem value="deposit" id={`${idPrefix}-amount-deposit`} />
          <span>
            <span className="block font-medium text-sm">Pay Deposit ({options.deposit_percent}%)</span>
            <span className="block text-xs text-muted-foreground">
              Balance of ${(options.total_amount - options.deposit_amount).toFixed(2)} due later
            </span>
          </span>
        </span>
        <span className="font-semibold text-sm">${options.deposit_amount.toFixed(2)}</span>
      </Label>
      <Label htmlFor={`${idPrefix}-amount-full`} className={OPTION_CLASS}>
        <span className="flex items-center gap-3">
          <RadioGroupItem value="full" id={`${idPrefix}-amount-full`} />
          <span>
            <span className="block font-medium text-sm">Pay in Full</span>
            <span className="block text-xs text-muted-foreground">Pay the whole amount now</span>
          </span>
        </span>
        <span className="font-semibold text-sm">${options.total_amount.toFixed(2)}</span>
      </Label>
    </RadioGroup>
  );
}
